import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import AdminKpiRecordService from './AdminKpiRecordService';
import WhtCertificateService from '../../company/whtCertificate/WhtCertificateService';

// Always two decimals. Part-time withholding is 3% of a whole-baht run, which
// almost never divides evenly (3% of 12,345 = 370.35), so amounts here carry
// satang and a row that hid them would not reconcile against the certificate.
const baht = (n) =>
  `฿${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthName = (m) => new Date(2000, m - 1).toLocaleString('default', { month: 'long' });

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// yyyy-mm-dd in local time — toISOString would shift the day in +07:00.
const toInputDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * When each run is actually paid.
 *
 * Run 3 is the one that catches people out: the 5th run for September is paid on
 * 5 OCTOBER, so its nominal date falls in the following month even though the
 * money belongs to September.
 *
 * A date landing on a weekend moves EARLIER, onto the preceding Friday. Being
 * paid early is fine; late is not, so the adjustment only ever goes backwards.
 */
const nominalPayDate = (run, month, year) => {
  if (run === 1) return new Date(year, month - 1, 15);
  if (run === 2) return new Date(year, month, 0); // day 0 of next month = last day of this one
  return new Date(year, month, 5); // 5th of the following month
};

const actualPayDate = (run, month, year) => {
  const d = nominalPayDate(run, month, year);
  while (isWeekend(d)) d.setDate(d.getDate() - 1);
  return d;
};

const RUN_TABS = [
  { run: 1, short: '15th' },
  { run: 2, short: 'End of month' },
  { run: 3, short: '5th' },
];

const shortDate = (value) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const PaymentTable = ({ rows, onMarkOne, onUnmark, onOpenCertificate, onOpenVoucher, busyAdminId }) => {
  if (rows.length === 0) {
    return <div className="p-6 text-center text-gray-400 text-sm">Nothing to pay on this run.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="py-2 px-4 text-left text-xs font-medium text-gray-700 uppercase">Admin</th>
            <th className="py-2 px-4 text-left text-xs font-medium text-gray-700 uppercase">Bank</th>
            <th className="py-2 px-4 text-right text-xs font-medium text-gray-700 uppercase">Amount</th>
            <th className="py-2 px-4 text-left text-xs font-medium text-gray-700 uppercase">Paid</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => {
            const isBusy = busyAdminId === row.adminId;
            return (
              <tr key={row.adminId} className={row.paymentDate ? 'bg-emerald-50/40' : 'hover:bg-gray-50'}>
                <td className="py-2 px-4 text-sm font-medium text-gray-900">
                  {row.adminName}
                  {row.isManuallyEdited && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800">edited</span>
                  )}
                  {row.employmentType === 'parttime' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800">part-time</span>
                  )}
                </td>
                <td className="py-2 px-4 text-xs text-gray-600">
                  {row.bankAccountNo ? (
                    <div>
                      <div className="font-medium">{row.bankName}</div>
                      <div>{row.bankAccountNo}</div>
                      {row.bankAccountName && <div className="text-gray-500">{row.bankAccountName}</div>}
                    </div>
                  ) : (
                    <span className="text-red-600 font-medium">No bank account</span>
                  )}
                </td>
                {/* The headline figure is what you type into the bank; everything
                    that came off it is itemised underneath, because the transfer
                    alone does not explain what the person earned. SS and SL only
                    appear on a run we computed — a paid run stores its net and its
                    tax and nothing else, so they are omitted there rather than
                    shown as a guess that would not add up to what was paid. */}
                <td className="py-2 px-4 text-sm text-right font-semibold whitespace-nowrap">
                  {baht(row.amount)}
                  {(row.withholdingTax > 0 || row.socialSecurity > 0 || row.studentLoan > 0) && (
                    <div className="text-xs font-normal text-gray-500">
                      {baht(row.beforeDeductions ?? row.gross)}
                      {row.socialSecurity > 0 && <> − {baht(row.socialSecurity)} SS</>}
                      {row.studentLoan > 0 && <> − {baht(row.studentLoan)} SL</>}
                      {row.withholdingTax > 0 && <> − {baht(row.withholdingTax)} WHT</>}
                    </div>
                  )}
                </td>
                <td className="py-2 px-4 text-xs whitespace-nowrap">
                  {row.paymentDate ? (
                    <div>
                      <div className="text-emerald-700 font-medium">
                        paid {shortDate(row.paymentDate)}
                        <button
                          onClick={() => onUnmark(row)}
                          disabled={isBusy}
                          className="ml-2 font-normal text-gray-400 hover:text-gray-600 underline disabled:text-gray-300"
                        >
                          undo
                        </button>
                      </div>
                      {row.markedPaidAt && (
                        <div className="text-gray-400">recorded {shortDate(row.markedPaidAt)}</div>
                      )}
                      <div className="mt-0.5 flex gap-2">
                        {row.whtCertificateId && (
                          <button
                            onClick={() => onOpenCertificate(row)}
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            50 ทวิ
                          </button>
                        )}
                        {/* Every part-time payment has one, including those that
                            withheld nothing or had no payee to certify. */}
                        {row.employmentType === 'parttime' && row.summaryId && (
                          <button
                            onClick={() => onOpenVoucher(row)}
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            ใบสำคัญจ่าย
                          </button>
                        )}
                      </div>
                      {/* Two different faults needing two different fixes: tax was
                          withheld but no payee exists to certify it, versus the
                          money went out untaxed — which only undoing the payment
                          can fix, since a paid run never recomputes. */}
                      {row.employmentType === 'parttime' && !row.whtCertificateId && (
                        row.withholdingTax > 0 ? (
                          <div className="text-amber-700">no certificate — link a WHT payee</div>
                        ) : (
                          <div className="text-red-600">
                            paid untaxed — undo and mark paid again
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => onMarkOne(row)}
                      disabled={isBusy}
                      className="text-indigo-600 hover:text-indigo-800 disabled:text-gray-300"
                    >
                      mark paid
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Warnings = ({ warnings, activeRun }) => {
  const items = [];

  if (warnings.notFrozen?.length) {
    const names = [...new Set(warnings.notFrozen.map((w) => w.adminName))];
    items.push({
      tone: 'red',
      text: `Run amounts were never stored for: ${names.join(', ')}. Regenerate the month — these are unknown, not zero.`,
    });
  }
  if (warnings.missingBank?.length) {
    items.push({
      tone: 'red',
      text: `No bank account on file for: ${warnings.missingBank.map((w) => w.adminName).join(', ')}.`,
    });
  }
  // Unpaid runs work themselves out from the admin record, so anything left here
  // is money that has already gone out untaxed — which recalculating cannot fix,
  // because a paid run is a record of a real transfer and never moves.
  if (warnings.partTimeNoWithholding?.length) {
    const names = warnings.partTimeNoWithholding.map((w) => w.adminName).join(', ');
    items.push({
      tone: 'red',
      text: `Already PAID with no tax withheld: ${names}. The full amount went out untaxed and no certificate exists for it. Undo that payment and mark it paid again to withhold the 3% and issue the certificate.`,
    });
  }
  if (warnings.manuallyEdited?.length) {
    items.push({
      tone: 'amber',
      text: `Hand-edited totals: ${warnings.manuallyEdited.map((w) => w.adminName).join(', ')}.`,
    });
  }
  // Only worth saying on the 5th run, where it explains who is missing from the
  // list. Base salary does not wait for the month to be generated, so on Runs 1
  // and 2 this would fire on every row, every month, and mean nothing.
  if (activeRun === 3 && warnings.noSummaryYet?.length) {
    items.push({
      tone: 'amber',
      text: `Not in this run — month not generated yet for: ${warnings.noSummaryYet.map((w) => w.adminName).join(', ')}. Generate the month once the bonus is known.`,
    });
  }

  if (items.length === 0) {
    return (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        Nothing to flag on this run.
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-2">
      {items.map((item, i) => (
        <div
          key={i}
          className={`rounded-lg border p-3 text-sm ${
            item.tone === 'red'
              ? 'border-red-200 bg-red-50 text-red-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
};

/**
 * Tenure milestones that first apply to the month being paid.
 *
 * Scoped to the month on purpose: reaching a year on 20 September means the
 * extra is first paid on 15 October, so it appears when you open October — not
 * the moment the anniversary passes, when there is nothing to do about it yet.
 */
const TenureMilestones = ({ pending, busyKey, onApply, onDismiss }) => {
  if (pending.length === 0) return null;

  return (
    <div className="mb-6 bg-white border border-violet-200 rounded-lg overflow-hidden">
      <div className="px-6 py-3 bg-violet-50 border-b border-violet-200">
        <h3 className="text-sm font-semibold text-violet-900">
          Tenure milestones starting this month ({pending.length})
        </h3>
        <p className="text-xs text-violet-800 mt-1">
          Each grants +1 leave day and +฿1,000 position allowance. Apply before the month is
          generated so the increase is in these runs — a run already generated needs recalculating,
          and one already paid is frozen until next month. Use "Already given" for one settled by
          hand; the stored numbers cannot tell the two apart.
        </p>
      </div>

      <ul className="divide-y divide-gray-200">
        {pending.map((p) => {
          const key = `${p.adminId}-${p.years}`;
          const isBusy = busyKey === key;
          return (
            <li key={key} className="px-6 py-3 flex items-center justify-between flex-wrap gap-2 text-sm">
              <div>
                <span className="font-medium">{p.adminName}</span>
                <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-violet-100 text-violet-800">
                  {p.years} year{p.years > 1 ? 's' : ''}
                </span>
                <div className="text-xs text-gray-500 mt-0.5">
                  started {p.startWorkingDate} · reached {p.dueOn} ·
                  {' '}leave {p.currentLeaveDays}→{p.newLeaveDays} days,
                  {' '}position {baht(p.currentPositionAllowance)}→{baht(p.newPositionAllowance)}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => onApply(p)}
                  disabled={isBusy}
                  className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50"
                >
                  Apply increase
                </button>
                <button
                  onClick={() => onDismiss(p)}
                  disabled={isBusy}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Already given
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const SalaryPayment = () => {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [activeRun, setActiveRun] = useState(1);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [busyAdminId, setBusyAdminId] = useState(null);
  const [tenurePending, setTenurePending] = useState([]);
  const [tenureBusyKey, setTenureBusyKey] = useState(null);
  // The settle date — when the money actually moves. Blank = today. Set it to
  // record a transfer made on a different day, e.g. an advance payment settled
  // before the nominal pay date.
  const [settleDate, setSettleDate] = useState('');

  const yearOptions = [];
  for (let y = now.getFullYear() + 1; y >= 2026; y--) yearOptions.push(y);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await AdminKpiRecordService.getPayrollReport(month, year);
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to load salary payment');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [month, year]);

  // Scoped to the month being paid, so a milestone shows up when it first
  // affects a payroll rather than the day the anniversary passes.
  const loadTenure = useCallback(async () => {
    try {
      const response = await AdminKpiRecordService.getTenureMilestones(month, year);
      setTenurePending(response.data.pending || []);
    } catch (error) {
      // Never let this block the payment list.
      console.error('Error loading tenure milestones:', error);
    }
  }, [month, year]);

  useEffect(() => {
    load();
    loadTenure();
  }, [load, loadTenure]);

  const resolveTenure = async (milestone, apply) => {
    const key = `${milestone.adminId}-${milestone.years}`;
    setTenureBusyKey(key);
    try {
      await AdminKpiRecordService.resolveTenureMilestone(milestone.adminId, milestone.years, apply);
      toast.success(
        apply
          ? `${milestone.adminName}: +1 leave day, +฿1,000 position allowance`
          : `${milestone.adminName}: ${milestone.years}-year milestone cleared, pay unchanged`
      );
      await loadTenure();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not update the milestone');
    } finally {
      setTenureBusyKey(null);
    }
  };

  const runAction = async (action, successMessage, adminId = null) => {
    setBusyAdminId(adminId ?? 'all');
    try {
      await action();
      if (successMessage) toast.success(successMessage);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Action failed');
    } finally {
      setBusyAdminId(null);
    }
  };

  // Report what actually happened. A request that marks nothing is not a success —
  // silently claiming otherwise is how a missed payment goes unnoticed.
  const reportMarkResult = ({ data }) => {
    const { marked = [], skipped = [], certificatesIssued = [], certificateWarnings = [] } = data;

    if (marked.length > 0) {
      toast.success(`Marked ${marked.length} payment(s) paid — ${baht(data.total)}`);
    }
    if (skipped.length > 0) {
      const reasons = skipped
        .map((s) => `${s.adminName} (${s.reason.replace(/-/g, ' ')})`)
        .join(', ');
      toast[marked.length > 0 ? 'warn' : 'error'](`Not marked: ${reasons}`);
    }
    if (marked.length === 0 && skipped.length === 0) {
      toast.error('Nothing was marked paid.');
    }

    if (certificatesIssued.length > 0) {
      toast.info(`Issued ${certificatesIssued.length} WHT certificate(s)`);
    }
    // The payment still went through — this says the paperwork did not, and why.
    // The two reasons need different fixes, so they get different messages.
    if (certificateWarnings.length > 0) {
      const explain = {
        'no-payee': 'link a WHT payee on their admin record',
        'no-withholding-stored':
          'no tax was withheld on this run. The 5th run is frozen when the month is generated, so recalculate the month before marking it paid.',
      };
      const byReason = {};
      certificateWarnings.forEach((w) => {
        byReason[w.reason] = byReason[w.reason] || [];
        byReason[w.reason].push(w.adminName);
      });
      Object.keys(byReason).forEach((reason) => {
        const names = byReason[reason].join(', ');
        toast.warn(`No WHT certificate for ${names}: ${explain[reason] || reason.replace(/-/g, ' ')}`);
      });
    }
  };

  const openVoucher = async (row) => {
    try {
      await AdminKpiRecordService.openVoucherPdf({ summaryId: row.summaryId, run: activeRun });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not open the voucher');
    }
  };

  const openCertificate = async (row) => {
    try {
      await WhtCertificateService.openCertificatesPdf({ ids: [row.whtCertificateId] });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not open the certificate');
    }
  };

  const markRows = (rows, adminId = null) =>
    runAction(
      async () => {
        const response = await AdminKpiRecordService.markRunPaid({
          month,
          year,
          run: activeRun,
          adminIds: rows.map((r) => r.adminId),
          paidAt: effectivePayDate,
        });
        reportMarkResult(response);
      },
      null,
      adminId
    );

  const markWholeRun = (unpaidRows) => markRows(unpaidRows);
  const markOne = (row) => markRows([row], row.adminId);

  const unmarkOne = (row) =>
    runAction(
      () => AdminKpiRecordService.unmarkRunPaid(row.summaryId, activeRun),
      `${row.adminName} no longer marked paid`,
      row.adminId
    );

  const m = parseInt(month);
  const y = parseInt(year);
  const run = report?.runs?.find((r) => r.run === activeRun);
  const payDate = actualPayDate(activeRun, m, y);
  const unpaidRows = (run?.rows || []).filter((r) => !r.paymentDate);
  const unpaidTotal = unpaidRows.reduce((sum, r) => sum + r.amount, 0);

  // The picker starts on the run's real pay date — nominal, pulled back off a
  // weekend — so the available day (13th, 14th…) is visible where it is chosen.
  const suggestedPayDate = toInputDate(payDate);
  const effectivePayDate = settleDate || suggestedPayDate;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4">
        <h2 className="text-5xl mb-4">Salary Payment</h2>

        {/* Car rent paid to the owners is not part of payroll: it is a separate
            transfer with its own 5% WHT certificate, so nothing here would flag it. */}
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <span className="font-semibold">อย่าลืมโอนค่าเช่ารถ</span> — แยกโอนต่างหากจากเงินเดือน
          (หัก ณ ที่จ่าย 5%) และออก 50 ทวิ ที่หน้า{' '}
          <a href="/company/wht-certificate" className="font-semibold underline">WHT Certificate</a>
        </div>

        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <p className="text-gray-600 mb-4 text-sm">
            One payment run at a time. These are the figures stored for the month, including any
            hand-set totals — not a fresh calculation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Salary month</label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mm) => (
                  <option key={mm} value={mm}>{monthName(mm)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {yearOptions.map((yy) => (
                  <option key={yy} value={yy}>{yy}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <p className="text-xs text-gray-500">
                A pay date landing on a weekend moves earlier, never later.
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : !report ? null : (
          <>
            <TenureMilestones
              pending={tenurePending}
              busyKey={tenureBusyKey}
              onApply={(m) => resolveTenure(m, true)}
              onDismiss={(m) => resolveTenure(m, false)}
            />

            {/* Month at a glance — what this month costs across all three runs. */}
            <div className="mb-4 flex flex-wrap gap-3 text-sm">
              {/* These read as buttons, so they behave as buttons — clicking a
                  run's total switches to that run. */}
              {report.runs.map((r) => (
                <button
                  key={r.run}
                  type="button"
                  onClick={() => setActiveRun(r.run)}
                  aria-pressed={r.run === activeRun}
                  className={`px-3 py-1.5 rounded border transition-colors ${
                    r.run === activeRun
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50'
                  }`}
                >
                  <span className="text-gray-500">{RUN_TABS.find((t) => t.run === r.run).short}: </span>
                  <span className="font-semibold">{baht(r.total)}</span>
                </button>
              ))}
              {report.whtGrandTotal > 0 && (
                <div className="px-3 py-1.5 rounded border border-amber-200 bg-amber-50">
                  <span className="text-gray-500">WHT this month: </span>
                  <span className="font-semibold text-amber-800">{baht(report.whtGrandTotal)}</span>
                </div>
              )}
              <div className="px-3 py-1.5 rounded border border-gray-200 bg-white ml-auto">
                <span className="text-gray-500">Month total: </span>
                <span className="font-bold text-indigo-700">{baht(report.grandTotal)}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="border-b border-gray-200 flex">
                {RUN_TABS.map((tab) => (
                  <button
                    key={tab.run}
                    onClick={() => setActiveRun(tab.run)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 -mb-px ${
                      tab.run === activeRun
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.short}
                  </button>
                ))}
              </div>

              <div className="px-6 py-4 border-b border-gray-200 flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-lg font-semibold">
                    Salary for {monthName(m)} {y}
                  </div>
                  <div className="text-xs text-gray-500">
                    {RUN_TABS.find((t) => t.run === activeRun).short} run
                    {activeRun === 3 && ' — paid the following month'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-indigo-600">{baht(run?.total)}</div>
                  <div className="text-xs text-gray-500">
                    {run?.rows.length || 0} {run?.rows.length === 1 ? 'payment' : 'payments'}
                    {run?.paidCount > 0 && ` · ${run.paidCount} paid`}
                  </div>
                </div>
              </div>

              {unpaidRows.length > 0 && (
                <div className="px-6 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between flex-wrap gap-3">
                  <span className="text-sm text-indigo-900">
                    {unpaidRows.length} still to transfer — {baht(unpaidTotal)}
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-indigo-900">Payment date</label>
                    <input
                      type="date"
                      value={effectivePayDate}
                      onChange={(e) => setSettleDate(e.target.value)}
                      className="px-2 py-1.5 text-sm border border-indigo-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={() => markWholeRun(unpaidRows)}
                      disabled={busyAdminId !== null}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {busyAdminId === 'all' ? 'Marking…' : `Mark all ${unpaidRows.length} paid`}
                    </button>
                  </div>
                </div>
              )}

              <div className="px-6 pt-4">
                <Warnings warnings={report.warnings || {}} activeRun={activeRun} />
              </div>

              <PaymentTable
                rows={run?.rows || []}
                onMarkOne={markOne}
                onUnmark={unmarkOne}
                onOpenCertificate={openCertificate}
                onOpenVoucher={openVoucher}
                busyAdminId={busyAdminId}
              />

              {(run?.rows.length || 0) > 0 && (
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm">
                  {run.whtTotal > 0 && (
                    <div className="flex justify-between text-gray-600 mb-1">
                      <span>Withheld this run (owed to the Revenue Department)</span>
                      <span>{baht(run.whtTotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span>Total to transfer</span>
                    <span className="text-indigo-700">{baht(run.total)}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SalaryPayment;
